import { doc, getDoc } from '../lib/offlineFirestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';

const idValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const clean = value.trim();
    if (!clean) return '';
    return clean.includes('/') ? clean.split('/').filter(Boolean).pop() || '' : clean;
  }
  if (value.id) return String(value.id);
  if (value.path) return String(value.path).split('/').filter(Boolean).pop() || '';
  return '';
};

const firstLogo = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

class LogoService {
  static logoCache = new Map();
  static CACHE_DURATION = 5 * 60 * 1000;

  static getCacheKey(tenantId = null) {
    return idValue(tenantId) || '__global__';
  }

  static readCached(tenantId = null) {
    const key = this.getCacheKey(tenantId);
    const cached = this.logoCache.get(key);
    if (!cached) return null;
    if ((Date.now() - cached.cachedAt) >= this.CACHE_DURATION) {
      this.logoCache.delete(key);
      return null;
    }
    return cached.logoUrl || null;
  }

  static cacheLogo(tenantId, logoUrl) {
    if (!logoUrl) return;
    this.logoCache.set(this.getCacheKey(tenantId), {
      logoUrl,
      cachedAt: Date.now()
    });
  }

  /**
   * Resolve the company logo for the current tenant.
   * Priority for SaaS isolation:
   * 1. Tenant/company branding
   * 2. App settings fallback
   * 3. System branding fallback
   */
  static async getLogo(tenantId = null) {
    try {
      const cached = this.readCached(tenantId);
      if (cached) return cached;

      const tenantValue = idValue(tenantId);
      let logoUrl = null;

      // Company/tenant logo MUST win when a tenant is known.
      if (tenantValue && COLLECTIONS?.TENANTS) {
        try {
          const tenantDoc = await getDoc(doc(db, COLLECTIONS.TENANTS, tenantValue));
          if (tenantDoc.exists()) {
            const tenant = tenantDoc.data() || {};
            logoUrl = firstLogo(
              tenant.logo,
              tenant.logoUrl,
              tenant.companyLogo,
              tenant.companyLogoUrl,
              tenant.branding?.logo,
              tenant.branding?.logoUrl
            );
          }
        } catch (error) {
          console.warn('Tenant logo lookup failed:', error);
        }
      }

      // Fallback to application settings.
      if (!logoUrl && COLLECTIONS?.SETTINGS) {
        try {
          const settingsDoc = await getDoc(doc(db, COLLECTIONS.SETTINGS, 'app_settings'));
          if (settingsDoc.exists()) {
            const settings = settingsDoc.data() || {};
            logoUrl = firstLogo(
              settings.logo,
              settings.logoUrl,
              settings.companyLogo,
              settings.branding?.logo,
              settings.branding?.logoUrl
            );
          }
        } catch (error) {
          console.warn('App settings logo lookup failed:', error);
        }
      }

      // Final system branding fallback.
      if (!logoUrl && COLLECTIONS?.SYSTEM_SETTINGS) {
        try {
          const systemSettingsDoc = await getDoc(doc(db, COLLECTIONS.SYSTEM_SETTINGS, 'branding'));
          if (systemSettingsDoc.exists()) {
            const branding = systemSettingsDoc.data() || {};
            logoUrl = firstLogo(
              branding.logo,
              branding.logoUrl,
              branding.companyLogo
            );
          }
        } catch (error) {
          console.warn('System branding logo lookup failed:', error);
        }
      }

      if (logoUrl) this.cacheLogo(tenantId, logoUrl);
      return logoUrl || null;
    } catch (error) {
      console.error('Error fetching logo:', error);
      return null;
    }
  }

  static clearCache(tenantId = null) {
    if (tenantId) {
      this.logoCache.delete(this.getCacheKey(tenantId));
      return;
    }
    this.logoCache.clear();
  }
}

export default LogoService;